import { useState } from 'react';
import { Button, Checkbox, InfoBox, Input, Modal } from '@immo/ui-kit';
import {
  VALUATION_AGENT_PERMISSIONS,
  type ValuationAgentPermission,
  type ValuationAgentTokenView,
} from '@immo/api-contract';
import { apiClient } from '@/lib/auth/api-client';
import { valuationAgentPermissionLabelKey } from '@/lib/valuation-agent/permission-label';
import { useTranslations } from '@/i18n/use-translations';
import { CopyIcon } from '@/components/layout/icons';

type Props = {
  /** Also the "Fertig" path: the page re-reads its token once the dialog has dropped its state. */
  readonly onClose: () => void;
  /** The active token being replaced: preselects its permissions and submits to `replace`. */
  readonly replacing?: ValuationAgentTokenView;
};

/**
 * Two phases in one component, like the MCP-access mint dialog: the permission list, then the show-once
 * secret. The secret lives only in this component's state; closing drops it and no endpoint returns it again.
 */
export function IssueTokenDialog({ onClose, replacing }: Props) {
  const t = useTranslations('pages.admin.valuation-agent');
  const tErrors = useTranslations('errors');
  const tIssue = useTranslations('pages.admin.mcp-access.issue');
  const title = replacing === undefined ? t('issue') : t('replace');
  const [checked, setChecked] = useState<ReadonlySet<ValuationAgentPermission>>(
    () => new Set(replacing?.permissions ?? VALUATION_AGENT_PERMISSIONS),
  );
  const [error, setError] = useState<string | null>(null);
  const [stale, setStale] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [issued, setIssued] = useState<string | null>(null);

  const fallbackError = replacing === undefined ? t('issue-error') : t('replace-error');

  const toggle = (permission: ValuationAgentPermission, on: boolean) => {
    const next = new Set(checked);

    if (on) next.add(permission);
    else next.delete(permission);

    setChecked(next);
    setError(null);
  };

  const submit = async () => {
    if (checked.size === 0) {
      setError(t('no-permission-chosen'));

      return;
    }

    setSubmitting(true);

    try {
      const permissions = VALUATION_AGENT_PERMISSIONS.filter((permission) => checked.has(permission));
      const result =
        replacing === undefined
          ? await apiClient.adminValuationAgentToken.issue({ body: { permissions } })
          : await apiClient.adminValuationAgentToken.replace({
              pathParams: { id: replacing.id },
              body: { permissions },
            });

      if (result.ok) {
        setIssued(result.body.secret);

        return;
      }

      const code = (result.body as { error?: { code?: string } } | undefined)?.error?.code;

      // A stale token id can only be refused again, so the confirm stays off until the page re-reads.
      if (code === 'identity.valuation-agent-token.not-active') setStale(true);

      setError(code === undefined ? fallbackError : tErrors(code));
    } catch {
      setError(fallbackError);
    } finally {
      setSubmitting(false);
    }
  };

  const copy = async () => {
    if (issued === null) return;

    try {
      await navigator.clipboard.writeText(issued);
    } catch {
      // Clipboard access can be denied; the secret stays visible for manual copy.
    }
  };

  if (issued !== null) {
    return (
      <Modal
        open
        onClose={onClose}
        size="md"
        title={title}
        footer={
          <Button type="button" onClick={onClose}>
            {tIssue('done-cta')}
          </Button>
        }
      >
        <div className="space-y-3">
          <InfoBox type="warning">{t('once-hint')}</InfoBox>
          <div className="flex items-center gap-2">
            <Input readOnly value={issued} className="font-mono" aria-label={t('title')} />
            <Button type="button" variant="secondary" onClick={copy}>
              <CopyIcon className="h-4 w-4" />
              {t('copy')}
            </Button>
          </div>
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      open
      onClose={onClose}
      size="md"
      title={title}
      footer={
        <>
          <Button variant="ghost" type="button" onClick={onClose} disabled={submitting}>
            {tIssue('cancel-cta')}
          </Button>
          <Button type="button" onClick={submit} disabled={submitting || stale}>
            {title}
          </Button>
        </>
      }
    >
      <fieldset className="space-y-3">
        <legend className="sr-only">{t('permissions-label')}</legend>
        {VALUATION_AGENT_PERMISSIONS.map((permission) => {
          const id = `valuation-agent-${valuationAgentPermissionLabelKey(permission)}`;

          return (
            <div key={permission} className="flex items-center gap-3">
              <Checkbox id={id} checked={checked.has(permission)} onCheckedChange={(on) => toggle(permission, on)} />
              <label htmlFor={id} className="text-base-sm text-text-primary">
                {t(`permission.${valuationAgentPermissionLabelKey(permission)}`)}
              </label>
            </div>
          );
        })}
      </fieldset>
      {error !== null && (
        <p role="alert" className="mt-3 text-base-sm text-danger-700">
          {error}
        </p>
      )}
    </Modal>
  );
}
